// Shared app layout with navigation sidebar and content area
import { Outlet, Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import {
  Home,
  FileText,
  Users,
  BarChart3,
  Settings,
  LogOut,
  Shield,
  PlusCircle,
  Search,
  Pencil,
  CreditCard,
  UserCheck,
} from "lucide-react";
import { AnimatedLogo } from "@/components/shared/AnimatedLogo";
import { ModeSwitcher } from "@/components/admin/ModeSwitcher";
import { useAdminMode } from "@/hooks/useAdminMode";
import { useAgentProfile } from "@/hooks/useAgentProfile";
import { api } from "@/services/api";
import { useUnreadCounts } from "@/hooks/useMessages";

interface NavItem {
  path: string;
  label: string;
  icon: React.ElementType;
  roles: string[];
}

const navItems: NavItem[] = [
  { path: "/owner", label: "My Requests", icon: FileText, roles: ["owner"] },
  { path: "/owner/new-request", label: "New Request", icon: PlusCircle, roles: ["owner"] },
  { path: "/agent", label: "Dashboard", icon: Home, roles: ["agent"] },
  { path: "/agent/browse", label: "Browse Requests", icon: Search, roles: ["agent"] },
  { path: "/agent/leads", label: "Active Leads", icon: UserCheck, roles: ["agent"] },
  { path: "/agent/subscription", label: "Subscription", icon: CreditCard, roles: ["agent"] },
  { path: "/admin", label: "Verification Queue", icon: Shield, roles: ["admin"] },
  { path: "/admin/inventory", label: "Inventory", icon: BarChart3, roles: ["admin"] },
];

export function AppLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const { activeMode, switchMode } = useAdminMode();
  const { profile } = useAgentProfile();
  const isAdmin = user?.role === "admin";
  const [ownerResponseCount, setOwnerResponseCount] = useState(0);
  const unreadCounts = useUnreadCounts(!!user);

  // Fetch total response count for owner badge
  useEffect(() => {
    if (user?.role !== "owner" && !(isAdmin && activeMode === "owner")) return;
    api.get<{ data: { response_count?: number }[] }>("/owner-requests", { limit: 50 })
      .then((res) => {
        const total = res.data.reduce((sum, r) => sum + (r.response_count || 0), 0);
        setOwnerResponseCount(total);
      })
      .catch(() => {});
  }, [user?.role, isAdmin, activeMode, location.pathname]);

  // Show menu items relevant to the current section (admin, agent, owner)
  let section: string | undefined = undefined;
  if (isAdmin) {
    // Admin mode-switching: show nav for the active mode
    section = activeMode;
  } else if (location.pathname.startsWith("/admin")) section = "admin";
  else if (location.pathname.startsWith("/agent")) section = "agent";
  else if (location.pathname.startsWith("/owner")) section = "owner";

  const filteredNav = section
    ? navItems.filter((item) => item.roles.includes(section!))
    : navItems;

  const isAgentSection = section === "agent";
  const isAdminSection = section === "admin";
  const displayName = isAgentSection && profile?.display_name
    ? profile.display_name
    : isAgentSection || isAdminSection ? (user?.name || user?.email) : null;
  const profileLink = isAgentSection ? "/agent/register" : isAdminSection ? "/admin" : "/owner";

  return (
    <div className="page-shell flex">
      {/* Sidebar */}
      <aside className="sidebar">
        <div className="p-6 border-b border-white/10">
          <Link to="/">
            <AnimatedLogo size={36} />
          </Link>
          {isAdmin && (
            <div className="mt-3">
              <ModeSwitcher activeMode={activeMode} onSwitch={switchMode} />
            </div>
          )}
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {filteredNav.map((item) => {
            const isActive = location.pathname === item.path;
            let badge = 0;
            if (item.path === "/owner" && ownerResponseCount > 0) badge = ownerResponseCount;
            if (item.path === "/owner" && unreadCounts.total > 0) badge += unreadCounts.total;
            if (item.path === "/agent/leads" && unreadCounts.total > 0) badge = unreadCounts.total;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={isActive ? "nav-link-active" : "nav-link"}
              >
                <item.icon className="h-5 w-5" />
                {item.label}
                {badge > 0 && (
                  <span className="ml-auto px-1.5 py-0.5 text-[10px] font-bold bg-sun text-midnight rounded-full leading-none">
                    {badge}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* User section */}
        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3 mb-3">
            <Link to={profileLink} className="group relative h-8 w-8 rounded-full bg-gold/15 flex items-center justify-center overflow-hidden shrink-0">
              {isAgentSection && profile?.profile_photo_url ? (
                <img src={profile.profile_photo_url} alt="" className="h-full w-full object-cover" />
              ) : (
                <span className="text-sm font-medium text-sun">
                  {displayName?.[0] || user?.email?.[0] || "U"}
                </span>
              )}
              <div className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <Pencil className="h-3.5 w-3.5 text-white" />
              </div>
            </Link>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">
                {displayName || "Anonymous Owner"}
              </p>
              <p className="text-xs text-white/50 capitalize">{user?.role}</p>
            </div>
          </div>
          <button
            onClick={logout}
            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-white/50 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="content-area">
        <div className="max-w-6xl mx-auto p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
